import { useState } from 'react';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import type { EnrollmentItem } from '@shared/flow/enrollment';
import { canonicalizeEmail } from '@shared/normalize-email';

import { contactDisplayIdentity } from '../../EnrollQueue/contactIdentity';
import { findConnectorField } from '../../EnrollQueue/connector-field';
import { isRecentSendFlagged } from '../../EnrollQueue/itemBlockReason';
import { PlayPicker } from '../../EnrollQueue/PlayPicker';
import { isItemOverridden } from '../../EnrollQueue/queue-group-send';
import { useIsSenderTouched } from '../../EnrollQueue/state/staged-play-config';
import { useReassignPlay } from '../../EnrollQueue/useReassignPlay';
import { WarningBadges } from '../../EnrollQueue/WarningBadges';
import { useContactStateDetail } from '../../contact/hooks';
import { blockPillFor, rowMeta } from './leadDisplay';
import { LeadExpandedBody, type LeadEditorContext } from './LeadExpandedBody';
import { RailRow } from './RailRow';

/**
 * Row statuses `AssignedRow.tsx`/`itemBlockReason.ts` treat as "mid-pipeline
 * or done" — D18/D34: no Send/Send-anyway action renders at all (the pill
 * alone is the row's whole story), and `queued` freezes exactly like
 * `sending` (terra-ohe.8, D34).
 */
const FROZEN_STATUSES: ReadonlySet<EnrollmentItem['status']> = new Set(['queued', 'sending', 'submitted']);

export interface LeadRowProps extends LeadEditorContext {
  /** D19 — `expandedItemId === item.id`, lifted to the pane so it survives a grouping/filter change. */
  expanded: boolean;
  /** PM1 — see `ContactRowMeta`'s matching prop doc; optional so a caller with no unstick path can still mount this row (the text blocks below still surface the status, just without the recovery action). */
  onUnstick?: (itemId: number) => void;
  unstickPending?: boolean;
  /** terra-ohe.8 — see `ContactRowMeta`'s matching prop doc. */
  onCancel?: (itemId: number) => void;
  cancelPending?: boolean;
  /**
   * rail-glaze-parity Change 3 — suppresses ONLY the `rowMeta(item, playName)`
   * subtitle line (`contact_title · account_name[ · playName]`), never the
   * `last_send_error`/`flagged_stale_at`/`queued` status blocks below it
   * (those are status info, not the subtitle). Default `false`. Set `true`
   * from `PlaySection.tsx`'s call site only — By Play's section header
   * already names the play and, unlike `ByContact.tsx`, never passes
   * `playName` to this row, so without this flag the meta line there is
   * JUST `contact_title · account_name` — redundant with the title row
   * directly above it. `ByContact.tsx` passes a real `playName` and keeps
   * this `false` (its default): that grouping has no other way to show which
   * play a row belongs to, so its meta line stays.
   */
  hideMetaSubtitle?: boolean;
}

/**
 * One lead in the V2 Plays pane (plan 2026-09-30-002, D16–D19). Built on
 * `RailRow`: title is `contactDisplayIdentity(item)` (D16), meta is
 * `rowMeta(item, playName)` plus the SAME stuck/queued/last-send-error
 * status information `AssignedRow.tsx`'s `ContactRowMeta` surfaces today (not
 * reused wholesale — `ContactRowMeta`'s own identity line would duplicate
 * this row's `title`/`meta` split; see this unit's Implementation Notes for
 * why) so no V1 status info is lost. Trailing, in fixed order: the
 * `Overridden` badge (D16, a null-check per E9 — never a diff against base
 * copy), the one block pill (D17), then the row's one action (D18) — never
 * more than one of Send/Send anyway, and none at all for a frozen row.
 *
 * Row click (anywhere but a real control) toggles the lifted `expandedItemId`
 * unless the item is busy in some surface's send pipeline (E28) — `RailRow`
 * owns the delegation guard and the busy-disables-toggle posture.
 *
 * rail-glaze-parity (2026-10-01, Change 2) — "Switch"/"Clear" live in
 * `trailing`, right before Send, so a rep can change or drop a lead's play
 * with no need to expand the row first. This row (not `LeadExpandedBody`)
 * now owns `reassignOpen`, `useReassignPlay`, and the inline reassign
 * `PlayPicker` mount — matching `UnassignedRow`'s own precedent of
 * co-locating a trigger button and its picker in ONE component, rather than
 * splitting them across a row/body pair as the old "Change Play" footer did.
 * "Clear" calls the SAME `onRemove` the old "Remove" footer button called,
 * with no confirm dialog — matching V1's `AssignedRow` row-level Remove.
 */
export function LeadRow(props: LeadRowProps) {
  const {
    item,
    playName,
    blockLabel,
    expanded,
    onToggleExpanded,
    readOnly,
    sendReadOnly,
    isItemBusy,
    handlers,
    identity,
    sending,
    inputOptions,
    onUnstick,
    unstickPending,
    onCancel,
    cancelPending,
    hideMetaSubtitle = false,
    onFlushSave,
    onClearStepOverrides,
    onReassign,
    requestQueueFlash,
    onRemove,
    plays,
    activePlayTypes,
    gongFlowsSuggested,
  } = props;

  const sendGate = sendReadOnly ?? readOnly;
  const itemBusy = isItemBusy(item.id);
  const senderTouched = useIsSenderTouched(identity, item.id);
  const overridden = isItemOverridden(item) || senderTouched;
  const pill = blockPillFor(blockLabel);
  const frozen = FROZEN_STATUSES.has(item.status);
  const name = contactDisplayIdentity(item);

  // D41 (rail-glaze-parity Change 2) — the inline "Switch" picker's own open
  // state. Lives HERE, not in `LeadExpandedBody`, so a rep can switch a
  // lead's play from a COLLAPSED row — same co-located trigger+picker shape
  // `UnassignedRow`'s own `pickerOpen` already uses.
  const [reassignOpen, setReassignOpen] = useState(false);
  // Same lazy, gated-on-open warm-intro read `UnassignedRow`'s own picker
  // and the old expanded-body reassign picker both performed.
  const { data: reassignStateDetail, isLoading: reassignStateDetailLoading, error: reassignStateDetailError } =
    useContactStateDetail(reassignOpen ? item.contact_id : null);
  const reassignWarmIntroAvailable =
    !reassignOpen || reassignStateDetailLoading || !!reassignStateDetailError || !Array.isArray(reassignStateDetail?.facts)
      ? true
      : reassignStateDetail.facts.some(fact => fact.dimension === 'warm_intro');

  const { reassign } = useReassignPlay({
    item,
    onFlushSave,
    onClearStepOverrides: itemId => onClearStepOverrides?.(itemId) ?? (() => {}),
    onReassign: (itemId, play) => onReassign?.(itemId, play),
    requestQueueFlash,
    // The stale-draft-unmount fix (`useReassignPlay`'s own doc comment):
    // close the item editor on reassign. This row's "editor" IS the expanded
    // body, gated on the lifted `expandedItemId` (D19) — but unlike the old
    // in-body mount, THIS call can now fire while the row is collapsed (a
    // "Switch" click never requires expanding first), so it must collapse
    // ONLY when actually expanded — `onToggleExpanded` is a flip, not a
    // one-way close, and calling it on an already-collapsed row would
    // accidentally expand it.
    setEditorOpen: open => { if (!open && expanded) onToggleExpanded(item.id); },
    setReassignOpen,
  });
  // Switch/Clear share one disabled gate — readOnly, busy-in-some-pipeline,
  // or frozen (mid-send/queued/already-sent) — same posture the old footer's
  // `controlsDisabled` used, now computed at the row (D16–D18's existing
  // `frozen` already covers `submitted` too, a superset of the footer's old
  // `sending`/`queued`-only check — tightening, not loosening).
  const switchClearDisabled = readOnly || itemBusy || frozen;
  // U5 — a warm_intro_id-persisting input option means this item sends via a
  // connector's own multithread request rather than a direct Terra email;
  // `findConnectorField` is the single shared predicate (`connector-field.ts`)
  // so this never re-derives differently from `EnrollmentItemEditor.tsx`'s
  // own "Request 1"/"Send 1" label flip (`hideSendButton` hides that block in
  // V2, so this row's own primary action is the only place the distinction
  // can show).
  const isRequest = !!findConnectorField(inputOptions);
  // FIX 1 (rail-glaze-parity QA round) — the three status blocks below are
  // the only multi-line meta content this row ever shows; when none apply,
  // `WarningBadges` (a single-line pill row) would be this row's ENTIRE meta
  // content, stacked under the title inside `RailRow`'s own `items-start`
  // column. That floats the pill above the vertical center of the title
  // instead of level with it. Moving it into `trailing` (a direct sibling of
  // the title column, already `items-center` internally, and — with no
  // status block — the same single-line height as the title) gets it level
  // "for free" from the outer row's shared top-alignment, with no change to
  // `RailRow`'s own default. When any status block IS present, the pill
  // stays nested under the title as before — it's already one of several
  // meta lines at that point, not a lone one needing to be leveled.
  const hasStatusBlock =
    !!item.last_send_error ||
    (!!item.flagged_stale_at && (item.status === 'sending' || item.status === 'queued')) ||
    (item.status === 'queued' && !item.flagged_stale_at);

  return (
    <RailRow
      contactId={item.contact_id}
      onToggle={() => onToggleExpanded(item.id)}
      toggleDisabled={itemBusy}
      title={name}
      meta={
        <>
          {!hideMetaSubtitle && <div>{rowMeta(item, playName)}</div>}
          {hasStatusBlock && <WarningBadges itemId={item.id} contactEmail={item.contact_email} />}
          {/* Ported verbatim (copy + condition) from `ContactRowMeta` — see
              that component's matching JSX for why each of these three
              blocks exists; not reused wholesale because its own identity
              line would duplicate this row's title/meta split (D16). */}
          {item.last_send_error && (
            <div
              role="alert"
              className="rounded-md bg-bg-red-light px-2 py-1.5 text-xs text-content-danger"
            >
              Last send failed: {item.last_send_error}
            </div>
          )}
          {item.flagged_stale_at && (item.status === 'sending' || item.status === 'queued') && (
            <div className="flex items-center justify-between gap-2 rounded-md bg-bg-orange-light px-2 py-1.5 text-xs text-content-warning">
              <span>Stuck — needs review</span>
              {onUnstick && (
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={readOnly || unstickPending}
                  title="Unlocks this item for editing and re-send. The interrupted send may have already reached the destination — verify before re-sending."
                  aria-label={`Reset ${name} to retry`}
                  onClick={(e) => { e.stopPropagation(); onUnstick(item.id); }}
                >
                  Reset to retry
                </Button>
              )}
            </div>
          )}
          {item.status === 'queued' && !item.flagged_stale_at && (
            <div className="flex items-center justify-between gap-2 rounded-md bg-bg-secondary px-2 py-1.5 text-xs text-content-tertiary">
              <span>Queued for send</span>
              {onCancel && (
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={readOnly || cancelPending}
                  title="Removes this item from the send queue before it starts sending."
                  aria-label={`Cancel ${name}`}
                  onClick={(e) => { e.stopPropagation(); onCancel(item.id); }}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
        </>
      }
      trailing={
        <>
          {/* FIX 1 — see `hasStatusBlock` comment above: the lone-pill case
              renders here instead of in `meta`, `inline` so it doesn't carry
              the stacked-under-a-title margin into this row. */}
          {!hasStatusBlock && (
            <WarningBadges itemId={item.id} contactEmail={item.contact_email} inline />
          )}
          {overridden && <Badge color="blue" size="xs">Overridden</Badge>}
          {pill && (
            <StatusBadge status={pill.status} size="xs" tooltipContent={pill.tooltip}>
              {pill.text}
            </StatusBadge>
          )}
          {/* rail-glaze-parity Change 2 — "Switch"/"Clear", relocated from
              `LeadExpandedBody`'s old expanded-only footer. Safe actions
              before the primary action (left-to-right convention), always
              rendered (never hidden on a frozen row — only disabled), same
              posture the old footer buttons had. */}
          <Button
            variant="secondary"
            size="xs"
            disabled={switchClearDisabled}
            aria-label={`${reassignOpen ? 'Close play picker for' : 'Switch play for'} ${name}`}
            onClick={() => setReassignOpen(o => !o)}
          >
            {reassignOpen ? 'Close' : 'Switch'}
          </Button>
          <Button
            variant="danger-secondary"
            size="xs"
            disabled={switchClearDisabled}
            aria-label={`Clear ${name} from queue`}
            onClick={() => onRemove?.(item.id)}
          >
            Clear
          </Button>
          {!frozen && (
            isRecentSendFlagged(item) ? (
              <Button
                variant="danger-secondary"
                size="xs"
                disabled={sendGate || itemBusy || item.status === 'sending' || item.status === 'queued'}
                aria-label={`Send anyway for ${name}`}
                onClick={() => { void handlers.handleSendAnyway(item.id); }}
              >
                Send anyway
              </Button>
            ) : (
              <Button
                variant="primary"
                size="xs"
                disabled={!!blockLabel || sendGate || itemBusy || sending}
                title={blockLabel ?? undefined}
                aria-label={`${isRequest ? 'Request' : 'Send'} to ${name}`}
                onClick={() => { void handlers.handleSendGroup([item.id]); }}
              >
                {isRequest ? 'Request' : 'Send'}
              </Button>
            )
          )}
        </>
      }
    >
      {expanded && <LeadExpandedBody {...props} sendReadOnly={sendGate} />}
      {reassignOpen && (
        // D31 — `data-no-row-toggle`: this picker sits inside the row's
        // click-to-toggle content column (`RailRow`), so a click on its own
        // non-button padding must not fall through and collapse/expand the
        // row out from under an in-progress reassign pick (same guard
        // `UnassignedRow`'s own inline picker uses).
        <div data-no-row-toggle>
          <PlayPicker
            plays={plays}
            activePlayTypes={activePlayTypes}
            gongFlowsSuggested={gongFlowsSuggested}
            disabled={readOnly}
            disabledReason="Enroll queue is read-only while impersonating"
            ariaLabelPrefix={`Reassign ${name} to`}
            contactHasEmail={canonicalizeEmail(item.contact_email) !== null}
            warmIntroAvailable={reassignWarmIntroAvailable}
            contactLabel={name}
            backLabel="← Queue"
            onAssign={reassign}
          />
        </div>
      )}
    </RailRow>
  );
}
